// semeia-disable-reexport
import type Store from "@ember-data/store";
import dayjs from "dayjs";
import type IntlService from "ember-intl/services/intl";
import { getGroupedBioExams } from "parog-web/components/bio-exam-results/utils/groupedBioExams";
import Prescription from "parog-web/components/prescription/utils/prescription";
import type BioExamOrder from "parog-web/models/bio-exam-order";
import BioExamPrescribedJournalEvent from "parog-web/models/bio-exam-prescribed-journal-event";
import GenericPrescriptionUploadedJournalEvent from "parog-web/models/generic-prescription-uploaded-journal-event";
import MedicalImagingPrescribedJournalEvent from "parog-web/models/medical-imaging-prescribed-journal-event";
import MedicationPrescribedJournalEvent from "parog-web/models/medication-prescribed-journal-event";
import ParamedicalCarePrescribedJournalEvent from "parog-web/models/paramedical-care-prescribed-journal-event";
import type PrescriptionJournalEvent from "parog-web/models/prescription-journal-event";
import type Timing from "parog-web/models/prescriptions/timing";
import TlsPrescribedJournalEvent from "parog-web/models/tls-prescribed-journal-event";

const dateFormat: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "long",
  year: "numeric"
};

function getActivePrescriptions<T extends PrescriptionJournalEvent>(
  journalEvents: Array<T>,
  /* After that a prescription duration has ended, how many days do we display it. */
  expirationDelayDays = 0
): Array<Prescription<T>> {
  return journalEvents
    .map(je => new Prescription(je))
    .filter(je => {
      if (!je.endDate) {
        return true;
      }
      const endDate = dayjs(je.endDate);
      endDate.add(expirationDelayDays, "days");
      return endDate.isAfter(new Date());
    });
}

function getActivePrescriptionJes<T extends PrescriptionJournalEvent>(
  journalEvents: Array<T>,
  /* After that a prescription duration has ended, how many days do we display */
  expirationDelayDays: number | undefined
): Array<T> {
  return getActivePrescriptions(journalEvents, expirationDelayDays).map(
    prescription => prescription.journalEvent
  );
}

function getBioExamPrescriptionContent(
  prescription: Prescription<BioExamPrescribedJournalEvent>,
  intl: IntlService,
  store: Store
): string {
  return bioExamPrescriptionContentFromParams(
    prescription.startDate,
    prescription.endDate,
    prescription.journalEvent.exams,
    prescription.journalEvent.frequency,
    prescription.journalEvent.customOrders,
    prescription.journalEvent.aldRelatedComments,
    intl,
    store
  );
}

function bioExamPrescriptionContentFromParams(
  startDate,
  endDate,
  exams,
  frequencyKey,
  customOrders,
  aldRelatedComments,
  intl,
  store
) {
  //If you update content here, think about updating it also in the back in `bio_exam_prescription.rb` file
  let content = `Le ${startDate.toLocaleDateString(
    "fr-FR",
    dateFormat
  )}. Examens à pratiquer :\n`;

  const orders = exams?.map(loinc =>
    store.peekRecord("bio-exam-order", loinc)
  ) as BioExamOrder[];

  const orderedOrders = orders ? getGroupedBioExams(orders) : [];

  orderedOrders.forEach((orderByLoincChapter, group) => {
    orderByLoincChapter.forEach((bioExamOrders, loincChapter) => {
      content += `\n${loincChapter} (${intl.t(
        "bio-exam-order.specimen." + group
      )})\n`;

      bioExamOrders.forEach(order => {
        if (order.customLabel) {
          content += `${order.customLabel}\n`;
        } else {
          content += `${order.label}\n`;
        }
      });
    });
  });

  if (customOrders) {
    content += `\n${customOrders}\n`;
  }

  if (frequencyKey && frequencyKey != "none") {
    const frequency = intl.t(`frequency.${frequencyKey}`);
    content += `\nÀ faire ${frequency}\n`;
  }

  content += "\n";

  if (aldRelatedComments) {
    content += `${aldRelatedComments}\n\n`;
  }

  const _endDate = new Date(endDate as Date);
  content += `Ordonnance valable jusqu'au ${_endDate.toLocaleDateString(
    "fr-FR",
    dateFormat
  )}.\n\n`;

  content +=
    "Merci de transmettre à présent les résultats au Dr Laurent Haas, Direction médicale Sêmeia : semeia@medical75.apicrypt.org";

  return content;
}

function getMedicationPrescriptionContent(
  prescription: Prescription<MedicationPrescribedJournalEvent>,
  intl: IntlService
) {
  //If you update content here, think about updating it also in the back in `medication_prescription.rb` file
  let content = `Le ${prescription.startDate.toLocaleDateString(
    "fr-FR",
    dateFormat
  )}.\n\n`;

  prescription.journalEvent.medications.forEach(medicationRequest => {
    content +=
      medicationRequest.medication?.longDescription ??
      medicationRequest.medication?.description;

    if (medicationRequest.dosage) {
      const doseQuantityTranslated = intl.t(
        `prescriptions.doseQuantities.${medicationRequest.dosage.doseQuantity}`
      );
      content += ` : ${doseQuantityTranslated} `;

      content +=
        medicationRequest.dosage.doseQuantity == 1 ? "unité" : "unités";

      if (medicationRequest.dosage.timing) {
        const timingKey = timingKeyFromObject(medicationRequest.dosage.timing);
        const timingTranslated = intl.t(`prescriptions.timings.${timingKey}`);
        content += `, ${timingTranslated}`;
      }
    }
    if (medicationRequest.comment) {
      content += `. ${medicationRequest.comment}`;
    }
    content += ".\n";
  });

  content += `\nQuantité suffisante pour ${
    prescription.journalEvent.validityPeriod.split("-")[0]
  } ${intl.t(
    "prescriptions.validity-period." +
      prescription.journalEvent.validityPeriod.split("-")[1]
  )}.`;

  if (prescription.journalEvent.otherMedicationsAld) {
    content += `\n\n${prescription.journalEvent.otherMedicationsAld}`;
  }

  return content;
}

function getMedicalImagingPrescriptionContent(
  prescription: Prescription<MedicalImagingPrescribedJournalEvent>
) {
  //If you update content here, think about updating it also in the back in `medical_imaging_prescription.rb` file
  let content = `Le ${prescription.startDate.toLocaleDateString(
    "fr-FR",
    dateFormat
  )}.\n`;

  if (prescription.journalEvent.typeOfExamAld) {
    content +=
      "\nCher confère / chère consoeur, merci de bien vouloir réaliser le / les examens suivants :";

    prescription.journalEvent.typeOfExamAld.split("\n").forEach(exam => {
      content += `\n\t - ${exam}`;
    });

    if (prescription.journalEvent.patientClinicalStateAld) {
      content += `\n\n État clinique : ${prescription.journalEvent.patientClinicalStateAld}`;
    }

    if (prescription.journalEvent.indicationsAld) {
      content += `\n\n Indications : ${prescription.journalEvent.indicationsAld}`;
    }
  }

  if (prescription.journalEvent.typeOfExamNonAld) {
    content +=
      "\nCher confère / chère consoeur, merci de bien vouloir réaliser le / les examens suivants :";

    prescription.journalEvent.typeOfExamNonAld.split("\n").forEach(exam => {
      content += `\n\t - ${exam}`;
    });

    if (prescription.journalEvent.patientClinicalStateNonAld) {
      content += `\n\n État clinique : ${prescription.journalEvent.patientClinicalStateNonAld}`;
    }

    if (prescription.journalEvent.indicationsNonAld) {
      content += `\n\n Indications : ${prescription.journalEvent.indicationsNonAld}`;
    }
  }

  return content;
}

function getParamedicalCarePrescriptionContent(
  prescription: Prescription<ParamedicalCarePrescribedJournalEvent>
) {
  //If you update content here, think about updating it also in the back in `paramedical_care_prescription.rb` file
  let content = `Le ${prescription.startDate.toLocaleDateString(
    "fr-FR",
    dateFormat
  )}.\n`;

  if (prescription.journalEvent.careAld) {
    content += `\n${prescription.journalEvent.careAld}`;
  }

  if (prescription.journalEvent.careNonAld) {
    content += `\n${prescription.journalEvent.careNonAld}`;
  }

  return content;
}

/**
 * @returns the timing key used in translation, _ex: "1-1-d"_
 */
function timingKeyFromObject(timing: Timing): string {
  let key = `${timing.frequency}-${timing.period}-${timing.periodUnit}`;
  if (timing.when && timing.when.length > 0) {
    timing.when.forEach(moment => {
      key += `-${moment}`;
    });
  }
  return key;
}

function getPrescriptionContent(
  prescription: Prescription<
    | BioExamPrescribedJournalEvent
    | MedicationPrescribedJournalEvent
    | MedicalImagingPrescribedJournalEvent
    | ParamedicalCarePrescribedJournalEvent
  >,
  intl: IntlService,
  store: Store
): string | undefined {
  if (prescription.journalEvent instanceof BioExamPrescribedJournalEvent) {
    return getBioExamPrescriptionContent(
      prescription as Prescription<BioExamPrescribedJournalEvent>,
      intl,
      store
    );
  } else if (
    prescription.journalEvent instanceof MedicationPrescribedJournalEvent
  ) {
    return getMedicationPrescriptionContent(
      prescription as Prescription<MedicationPrescribedJournalEvent>,
      intl
    );
  } else if (
    prescription.journalEvent instanceof MedicalImagingPrescribedJournalEvent
  ) {
    return getMedicalImagingPrescriptionContent(
      prescription as Prescription<MedicalImagingPrescribedJournalEvent>
    );
  } else if (
    prescription.journalEvent instanceof ParamedicalCarePrescribedJournalEvent
  ) {
    return getParamedicalCarePrescriptionContent(
      prescription as Prescription<ParamedicalCarePrescribedJournalEvent>
    );
  }

  return undefined;
}

function getPrescriptionFileName(
  prefix: string,
  prescriptionJE:
    | BioExamPrescribedJournalEvent
    | MedicationPrescribedJournalEvent
    | MedicalImagingPrescribedJournalEvent
    | ParamedicalCarePrescribedJournalEvent
    | GenericPrescriptionUploadedJournalEvent
    | TlsPrescribedJournalEvent
) {
  return `${prefix}-${prescriptionJE.patient.firstName}-${prescriptionJE.patient.lastName}-${prescriptionJE.id}.pdf`;
}

function getPrescriptionJournalEventComponent(
  journalEvent: PrescriptionJournalEvent
) {
  switch (journalEvent.constructor) {
    case BioExamPrescribedJournalEvent:
      return "bio-exam-display";
    case MedicationPrescribedJournalEvent:
      return "medication-display";
    case MedicalImagingPrescribedJournalEvent:
      return "medical-imaging-display";
    case ParamedicalCarePrescribedJournalEvent:
      return "paramedical-care-display";
    case GenericPrescriptionUploadedJournalEvent:
      return "generic-display";
    case TlsPrescribedJournalEvent:
      return "tls-display";
    default:
      return undefined;
  }
}

export {
  bioExamPrescriptionContentFromParams,
  getActivePrescriptionJes,
  getActivePrescriptions,
  getPrescriptionContent,
  getPrescriptionFileName,
  getPrescriptionJournalEventComponent
};
